import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, GenesisUnitOfWork } from 'app/core/core.module';
import {
    PartConfigAxle,
    PartConfigBrake,
    PartConfigCrossArm,
    PartConfigModelSize,
    PartConfigModelType,
    PartConfigOption,
    PartConfigPlaceQuantity,
    PartConfigSuspension,
    PartConfigTire,
    PartConfigWinchStand,
    PartConfigWinchStandOption,
} from 'app/core/model/entity-model';
import { Part } from 'app/core/model/part';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert } from 'devextreme/ui/dialog';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { PartOptionDialogComponent } from '../part-option-dialog.component';

@Component({
    selector: 'app-part-number-dialog',
    templateUrl: './part-number-dialog.component.html',
    styleUrls: ['./part-number-dialog.component.scss'],
    standalone: false,
})
export class PartNumberDialogComponent implements OnInit {
    modalService = inject(BsModalService);

    result: Subject<any> = new Subject<any>();
    partSelection!: {
        model: PartConfigModelType | null | undefined;
        size: PartConfigModelSize | null;
        modelTypeId: number | null;
        sizeId: number | null;
        axleId: number | null;
        brakeType: PartConfigBrake | null;
        brakeCount: number | null;
        brake1Id: number | null;
        brake2Id: number | null;
        brake3Id: number | null;
        brake1Disabled: boolean | null;
        brake2Disabled: boolean | null;
        brake3Disabled: boolean | null;
        brakeTypeId: number | null;
        tireId: number | null;
        suspension: PartConfigSuspension | null;
        suspensionId: number | null;
        optionsId: number | null;
        winchStand: PartConfigWinchStand | null;
        winchStandId: number | null;
        tailOption: string | null;
        toonOption: string | null;
        bunkBracketOption: string | null;
        bunkCoveringOption: string | null;
        crossArmId: number | null;
        placeQuantityId: number | null;
        winchStandOptionId: number | null;
        axleSuspension: string;
    };
    partNumber: any = ['XXX', 'XX', 'XXX', 'X', 'X', 'X'];

    partNumberString!: string;

    modelTypes: PartConfigModelType[] = [];
    modelSizes: PartConfigModelSize[] = [];
    axles: PartConfigAxle[] = [];
    brakes: PartConfigBrake[] = [];
    brakeOptions: PartConfigBrake[] = [];
    tires: PartConfigTire[] = [];
    suspensions: PartConfigSuspension[] = [];
    suspensionTires: PartConfigTire[] = [];
    winchStands: PartConfigWinchStand[] = [];
    winchStandsDropdown: any[] = [];
    winchStandOptions: PartConfigWinchStandOption[] = [];
    crossArms: PartConfigCrossArm[] = [];
    placeQuantities: PartConfigPlaceQuantity[] = [];
    placeQuantityOptions: PartConfigPlaceQuantity[] = [];
    options: PartConfigOption[] = [];

    matchingModelSizes: any;
    matchingAxles: any;
    matchingTires: any;
    brakeCount: any;
    IdlerAxleId: number = 1;
    TorsionIdlerAxleId: number = 5;
    DiscBrakeTireId: number = 6;
    sizeLinks: any;

    matchingParts: Part[] = [];
    selectedParts: any = [];
    showSideBar = false;
    invalidId!: number;
    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    upgradesEnabled = true;

    isAssemblyOptions = ['True', 'False'];
    processing = false;

    @ViewChild('partsGrid') partsGrid!: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    get showAxleSection(): boolean {
        return !this.partSelection?.modelTypeId || !!this.partSelection?.model?.hasAxles;
    }

    get hasTorsionOption(): boolean {
        const sizeBrakes = this.brakes.filter((x) => this.partSelection.size?._brakes?.some((b) => b.entityId === x.id));
        return sizeBrakes.some((x) => x.isTorsionSuspension);
    }

    get showOptionSegments(): boolean {
        return (
            !this.partSelection?.modelTypeId ||
            !!this.partSelection?.model?.hasTailOption ||
            !!this.partSelection?.model?.hasToonOption ||
            !!this.partSelection?.model?.hasPolyCarpetOption ||
            !!this.partSelection?.model?.hasWinchStand ||
            !!this.partSelection?.model?.hasTreeOptions ||
            (!!this.partSelection?.model?.hasConfigOptions && (this.partSelection?.size?._options?.length ?? 0) > 0)
        );
    }

    get stepNumbers(): { axle: number; gear: number; body: number; hardware: number } {
        let s = 1; // Trailer Base is always step 1
        const axleVisible = this.showAxleSection;
        const bodyVisible =
            !this.partSelection?.modelTypeId ||
            !!this.partSelection?.model?.hasTailOption ||
            !!this.partSelection?.model?.hasToonOption ||
            !!this.partSelection?.model?.hasPolyCarpetOption;
        const hardwareVisible =
            !this.partSelection?.modelTypeId ||
            !!this.partSelection?.model?.hasWinchStand ||
            !!this.partSelection?.model?.hasTreeOptions ||
            (!!this.partSelection?.model?.hasConfigOptions && (this.partSelection?.size?._options?.length ?? 0) > 0);

        const axleStep = axleVisible ? ++s : null;
        const gearStep = ++s;
        const bodyStep = bodyVisible ? ++s : null;
        const hardwareStep = hardwareVisible ? ++s : null;
        return { axle: axleStep, gear: gearStep, body: bodyStep, hardware: hardwareStep };
    }

    get calculatedPrice(): number | null {
        const basePrice = this.partSelection?.size?.basePrice;
        if (basePrice == null) return null;
        let price = basePrice;
        const getIncrease = (arr: any[], id: number | null) => {
            if (!id || !arr) return 0;
            return arr.find((x) => x.entityId === id)?.priceIncrease ?? 0;
        };
        price += getIncrease(this.partSelection.size._axleSizes, this.partSelection.axleId);
        price += getIncrease(this.partSelection.size._suspensions, this.partSelection.suspensionId);
        price += getIncrease(this.partSelection.size._tireSizes, this.partSelection.tireId);
        price += getIncrease(this.partSelection.size._winchStands, this.partSelection.winchStandId);
        price += getIncrease(this.partSelection.size._options, this.partSelection.optionsId);
        price += getIncrease(this.partSelection.size._placeQuantities, this.partSelection.placeQuantityId);
        if (this.partSelection.tailOption === 'Yes') price += this.partSelection.size.tailYesPrice ?? 0;
        if (this.partSelection.toonOption === 'Pontoon') price += this.partSelection.size.toonPontoonPrice ?? 0;
        if (this.partSelection.toonOption === 'Tritoon') price += this.partSelection.size.toonTritoonPrice ?? 0;
        const brake = this.partSelection.brakeType;
        if (brake) {
            price += brake.basePrice ?? 0;
            const brakeCount = this.partSelection.brakeCount ?? 0;
            if (brakeCount >= 2) price += brake.price2Axle ?? 0;
            if (brakeCount >= 3) price += brake.price3Axle ?? 0;
        }
        return price;
    }

    constructor(
        public modalRef: BsModalRef,
        private uow: GenesisUnitOfWork,
        private appInfo: AppInformationService,
    ) {}

    async ngOnInit() {
        this.clear();
        await this.getData();
    }

    async getData() {
        this.modelTypes = await this.uow.partConfigModelTypes.whereActive('displayOrder');
        this.modelSizes = await this.uow.partConfigModelSizes.whereActive('displayOrder', 'standardWinchStand');
        this.brakes = await this.uow.partConfigBrakes.whereActive('id');
        this.brakeOptions = this.brakes.filter((x) => !x.name?.toLowerCase().includes('idler'));
        this.axles = await this.uow.partConfigAxles.whereActive('id');
        this.suspensions = await this.uow.partConfigSuspensions.whereActive('displayOrder', 'partConfigSuspensionTireLinks.partConfigTire');
        this.tires = await this.uow.partConfigTires.whereActive('displayOrder', 'spareTire');
        this.winchStands = await this.uow.partConfigWinchStands.whereActive('id', 'partConfigWinchStandOptionLinks.partConfigWinchStandOption');
        this.winchStands.forEach((entity) => {
            entity.setOptionLinks(entity.partConfigWinchStandOptionLinks);
        });
        this.winchStandsDropdown = this.winchStands;
        this.winchStandOptions = await this.uow.partConfigWinchStandOptions.whereActive('id');
        this.crossArms = await this.uow.partConfigCrossArms.whereActive('id');
        this.placeQuantities = await this.uow.partConfigPlaceQuantities.whereActive('id');
        this.placeQuantityOptions = this.placeQuantities;
        this.options = await this.uow.partConfigOptions.whereActive('id');
        this.sizeLinks = await this.uow.partConfigSizeLinks.whereActive('id');
    }

    onToolbarPreparing(e: any) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarText',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    generatePartNumber() {
        //need to determine how many segments to show based on options available for the model, ie winch stand and tail options use the same segment, so if no winch stand options show then we can show the tail option segment, otherwise we need to combine them into one segment
        const segments = this.showOptionSegments ? (this.partSelection.size?.noWinchStand ? this.partNumber.slice(0, 5) : this.partNumber) : this.partNumber.slice(0, 4);
        this.partNumberString = segments.join('-');
        const firstDashIndex = this.partNumberString.indexOf('-');
        if (firstDashIndex !== -1) {
            this.partNumberString = this.partNumberString.slice(0, firstDashIndex) + this.partNumberString.slice(firstDashIndex + 1);
        }
        if (this.searchDebounceTimer !== null) clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = setTimeout(() => this.search(), 500);
    }

    onModelTypeChanged(e: any) {
        // console.log(e);
        this.clear();
        if (!!e.value) {
            this.partSelection.modelTypeId = e.value;
            this.partSelection.model = this.modelTypes.find((x) => x.id === e.value);
            this.matchingModelSizes = this.modelSizes.filter((x) => x.partConfigModelTypeId === e.value).sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
            this.partNumber[0] = this.modelTypes.find((x) => x.id === e.value).name;
            if (this.partSelection.model?.hasPolyCarpetOption) {
                this.partSelection.bunkCoveringOption = 'Carpet';
                this.partNumber[5] = '1';
            }
        }
        this.generatePartNumber();
    }

    async onSizeChanged(e) {
        this.partSelection = {
            modelTypeId: this.partSelection.modelTypeId,
            model: this.partSelection.model,
            sizeId: this.partSelection.sizeId,
            size: this.partSelection.size,
            axleId: null,
            brakeType: null,
            brakeCount: null,
            brake1Id: null,
            brake2Id: null,
            brake3Id: null,
            brake1Disabled: false,
            brake2Disabled: false,
            brake3Disabled: false,
            brakeTypeId: null,
            suspension: null,
            suspensionId: null,
            optionsId: null,
            winchStand: null,
            winchStandId: null,
            tireId: null,
            tailOption: null,
            toonOption: null,
            bunkBracketOption: null,
            bunkCoveringOption: null,
            crossArmId: null,
            placeQuantityId: null,
            winchStandOptionId: null,
            axleSuspension: 'Leaf Spring',
        };
        this.partNumber[2] = 'XXX';
        this.partNumber[3] = 'X';
        this.partNumber[4] = 'X';
        this.partNumber[5] = 'X';
        if (!!e.value) {
            this.partSelection.size = this.modelSizes.find((x) => x.id === e.value) as PartConfigModelSize;

            this.partSelection.size.setSizeLinks(
                this.sizeLinks.filter((x) => x.partConfigModelSizeId === e.value),
                this.axles,
                this.tires,
                this.suspensions,
                this.options,
                this.winchStands,
                this.placeQuantities,
                this.brakes,
            );
            const partAddonLinks = this.sizeLinks.filter((x) => x.partConfigModelSizeId === e.value && x.entityTypeName === 'Part' && !x.deleted);
            this.partSelection.size._salesAddons = partAddonLinks.map((l) => ({ entityId: l.primaryKey, sizeLinkId: l.id, name: `Part #${l.primaryKey}` }));
            // console.log(this.partSelection.size);
            this.partSelection.axleId = null;
            this.partSelection.brakeTypeId = null;
            this.partSelection.brakeCount = null;
            const sizeBrakes = this.brakes.filter((x) => this.partSelection.size._brakes?.some((b) => b.entityId === x.id));
            this.brakeOptions = sizeBrakes.filter((x) => !x.name?.toLowerCase().includes('idler'));
            if (this.partSelection.size._placeQuantities.length > 0) {
                this.placeQuantityOptions = this.placeQuantities.filter((x) => this.partSelection.size._placeQuantities.some((pq) => pq.entityId === x.id));
            } else {
                this.placeQuantityOptions = this.placeQuantities;
            }

            if (this.partSelection.size._axleSizes.length === 1 || this.partSelection.size.noBrakeOption) {
                this.partSelection.axleId = this.partSelection.size._axleSizes[0]?.entityId;
                this.partSelection.brake1Id = this.IdlerAxleId;
                this.partSelection.brakeTypeId = this.IdlerAxleId;
                this.partSelection.brakeCount = 0;
            }
            if (this.partSelection.size._tireSizes.length === 1) {
                this.partSelection.tireId = this.partSelection.size._tireSizes[0]?.entityId;
            }
            if (this.partSelection.size.noTailOption) {
                this.partSelection.tailOption = 'No';
            }
            if (this.partSelection.size._suspensions.length === 1) this.partSelection.suspensionId = this.partSelection.size._suspensions[0]?.entityId;
            if (this.partSelection.size._options.length === 1) {
                this.partSelection.optionsId = this.partSelection.size._options[0]?.entityId;
            } else {
                const defaultOption = this.partSelection.size._options.find((x) => x.isDefault);
                if (defaultOption) this.partSelection.optionsId = defaultOption.entityId;
            }
            if (this.partSelection.optionsId) {
                const slot = this.partSelection.size?.noWinchStand ? 4 : 5;
                const option = this.options.find((x) => x.id === this.partSelection.optionsId);
                if (option) this.partNumber[slot] = option.value;
            }

            this.winchStandsDropdown = this.partSelection.size?._winchStands;
            if (this.partSelection.size.standardWinchStandId) {
                this.upgradesEnabled = false;
                this.winchStandsDropdown.push({ entityId: this.partSelection.size.standardWinchStandId, name: this.partSelection.size.standardWinchStand.name + ' - STANDARD' });
                this.partSelection.winchStandId = this.partSelection.size.standardWinchStandId;
                if (this.partSelection.size.standardWinchStandOptionId) {
                    this.partSelection.winchStandOptionId = this.partSelection.size.standardWinchStandOptionId;
                }
            } else if (this.partSelection.size._winchStands.length === 1) {
                // turn off setting the winch stand if there is only one after talk with Mike on 4/24/26.
                // this.partSelection.winchStandId = this.partSelection.size._winchStands[0]?.entityId;
                // this.upgradesEnabled = true;
            } else {
                this.upgradesEnabled = true;
            }

            this.partNumber[1] = this.partSelection.size.name;
        } else {
            this.placeQuantityOptions = this.placeQuantities;
            this.brakeOptions = this.brakes.filter((x) => !x.name?.toLowerCase().includes('idler') && this.partSelection.size?._brakes?.some((b) => b.entityId === x.id));
            this.partNumber[1] = 'XX';
            this.winchStandsDropdown = [];
        }

        this.generatePartNumber();
    }

    onAxleChanged(e) {
        // console.log(e);
        if (!this.partSelection.axleId) return;
        this.brakeCount = [0];
        const axleArray = [];
        const axleCount = this.axles.find((x) => x.id === this.partSelection.axleId).axleCount;
        for (let index = 1; index <= axleCount; index++) {
            const brake = this.partSelection[`brake${index}Id`];
            // console.log('brake', brake);

            const brakeValue = brake ? this.brakes.find((x) => x.id === brake)?.value : null;
            axleArray.push(brakeValue ?? 'X');
            this.brakeCount.push(index);
        }
        this.partNumber[2] = axleArray.join('');
        this.onAxleSuspensionChanged({ value: this.partSelection.axleSuspension });
        this.generatePartNumber();
    }

    updateBrakeOptions(): void {
        const sizeBrakes = this.brakes.filter((x) => this.partSelection.size?._brakes?.some((b) => b.entityId === x.id));
        let filtered: PartConfigBrake[];
        if (this.partSelection.axleSuspension) {
            const isTorsion = this.partSelection.axleSuspension === 'Torsion Spring';
            filtered = sizeBrakes.filter((x) => !!x.isTorsionSuspension === isTorsion);
        } else {
            filtered = sizeBrakes;
        }
        if (this.partSelection.brakeCount === 0) {
            this.brakeOptions = filtered;
        } else {
            this.brakeOptions = filtered.filter((x) => !x.name?.toLowerCase().includes('idler'));
        }
    }

    onBrakeCountChanged(e) {
        // console.log(e);
        this.updateBrakeOptions();
        const idlerId = this.partSelection.axleSuspension === 'Torsion Spring' ? this.TorsionIdlerAxleId : this.IdlerAxleId;
        if (this.partSelection.brakeCount === 0) {
            this.partSelection.brakeTypeId = idlerId;
        }

        if (!this.partSelection.axleId || !this.partSelection.brakeTypeId) return;
        const axleCount = this.axles.find((x) => x.id === this.partSelection.axleId).axleCount;

        // set the brake axles
        let i = 0;
        const selectedBrakeType = this.brakes.find((x) => x.id === this.partSelection.brakeTypeId);
        for (let index = 1; index <= this.partSelection.brakeCount; index++) {
            // console.log('index', index);
            this.partSelection[`brake${index}Id`] = selectedBrakeType?.id ?? null;
            this.partSelection[`brake${index}Disabled`] = true;
            // i++;
        }

        if (axleCount !== this.partSelection.brakeCount) {
            for (let index = this.partSelection.brakeCount + 1; index <= axleCount; index++) {
                // console.log('index', index);
                this.partSelection[`brake${index}Id`] = idlerId;
                this.partSelection[`brake${index}Disabled`] = true;
            }
        }
        this.onAxleChanged(e);
        // this.generatePartNumber();
    }

    onBrakeTypeChanged(e) {
        this.partSelection.brakeType = this.brakes.find((x) => x.id === e.value);
        if (this.partSelection.brakeType?.disableTireSelection) {
            this.partSelection.tireId = this.DiscBrakeTireId;
        }
        this.onBrakeCountChanged(e);
    }

    onAxleSuspensionChanged(e) {
        this.updateBrakeOptions();
        // reset brake type if it's no longer in the filtered list
        if (this.partSelection.brakeTypeId && !this.brakeOptions.some((x) => x.id === this.partSelection.brakeTypeId)) {
            this.partSelection.brakeTypeId = null;
            this.partSelection.brakeType = null;
        }
    }

    async onSuspensionChanged(e) {
        // console.log('suspension', e);
        const suspension = this.suspensions.find((x) => x.id === e.value);
        if (suspension) {
            this.partSelection.suspension = suspension;
            this.suspensionTires = suspension.partConfigSuspensionTireLinks.filter((x) => !x.deleted).map((x) => x.partConfigTire);
            this.partNumber[2] = suspension.value;
            if (this.suspensionTires.length === 1) {
                this.partSelection.tireId = this.suspensionTires[0].id;
            }
        } else {
            this.partSelection.suspension = null;
            this.partNumber[2] = 'X';
        }

        this.generatePartNumber();
    }

    onWinchStandChanged(e) {
        // console.log('winchStand', e);
        const winchStand = this.winchStands.find((x) => x.id === e.value);
        if (winchStand) {
            this.partNumber[4] = winchStand.value;
            this.partSelection.winchStand = winchStand;
            if (winchStand._options.length == 1) {
                this.partSelection.winchStandOptionId = winchStand._options[0].optionId;
            }
        } else {
            this.partNumber[4] = 'X';
            this.partSelection.winchStand = null;
        }

        this.generatePartNumber();
    }
    onWinchStandOptionChanged(e) {
        const winchStandOption = this.winchStandOptions.find((x) => x.id === e.value);
        if (winchStandOption) this.partNumber[5] = winchStandOption.value;
        else this.partNumber[5] = 'X';

        this.generatePartNumber();
    }
    onOptionsChanged(e) {
        // console.log('options', e);
        const option = this.options.find((x) => x.id === e.value);
        const slot = this.partSelection.size?.noWinchStand ? 4 : 5;
        if (option) this.partNumber[slot] = option.value;
        else this.partNumber[slot] = 'X';
        this.generatePartNumber();
    }
    onTireChanged(e) {
        // console.log('tires', e);
        if (e.value) this.partNumber[3] = e.value;
        else this.partNumber[3] = 'X';
        this.generatePartNumber();
    }
    onTailOptionChanged(e) {
        // console.log(e);
        if (!!e.value && e.value === 'Yes') {
            this.partSelection.toonOption = 'Tritoon';
        }
        this.bunkStyle();
        this.generatePartNumber();
    }
    onToonOptionChanged(e) {
        // console.log(e);
        if (!!e.value && e.value === 'Pontoon') {
            this.partSelection.bunkBracketOption = 'Regular';
        }
        this.bunkStyle();
        this.generatePartNumber();
    }
    onBunkBracketOptionChanged(e) {
        console.log(e);
        this.bunkStyle();
        this.generatePartNumber();
    }
    bunkStyle() {
        if (this.partSelection.tailOption === 'No' && this.partSelection.toonOption === 'Pontoon') {
            this.partNumber[4] = '1';
        } else if (this.partSelection.tailOption === 'No' && this.partSelection.toonOption === 'Tritoon') {
            if (this.partSelection.bunkBracketOption === 'Regular') {
                this.partNumber[4] = '2';
            } else if (this.partSelection.bunkBracketOption === 'Adjustable') {
                this.partNumber[4] = '3';
            }
        } else if (this.partSelection.tailOption === 'Yes' && this.partSelection.toonOption === 'Tritoon') {
            if (this.partSelection.bunkBracketOption === 'Regular') {
                this.partNumber[4] = '4';
            } else if (this.partSelection.bunkBracketOption === 'Adjustable') {
                this.partNumber[4] = '5';
            }
        }
    }
    onBunkCoveringOptionChanged(e) {
        // console.log(e);
        this.partNumber[5] = e.value === 'Carpet' ? '1' : '2';
        this.generatePartNumber();
    }

    onPlaceQuantityOptionChanged(e) {
        console.log(e);
        if (e.value) {
            const place = this.placeQuantities.find((x) => x.id === e.value);
            this.partNumber[4] = place ? place.value : 'X';
        } else this.partNumber[4] = 'X';
        this.generatePartNumber();
    }
    onCrossArmOptionChanged(e) {
        // console.log(e);
        if (e.value) {
            const crossArm = this.crossArms.find((x) => x.id === e.value);
            this.partNumber[5] = crossArm ? crossArm.value : 'X';
        } else this.partNumber[5] = 'X';
        this.generatePartNumber();
    }

    async search() {
        if (this.partSelection.modelTypeId && this.partSelection.sizeId) this.matchingParts = await this.uow.getPartsByPartNumberRegex(this.partNumberString);
    }

    clear() {
        this.partSelection = {
            modelTypeId: null,
            model: null,
            sizeId: null,
            size: null,
            axleId: null,
            brakeType: null,
            brakeCount: null,
            brake1Id: null,
            brake2Id: null,
            brake3Id: null,
            brake1Disabled: false,
            brake2Disabled: false,
            brake3Disabled: false,
            brakeTypeId: null,
            suspension: null,
            suspensionId: null,
            optionsId: null,
            winchStand: null,
            winchStandId: null,
            tireId: null,
            tailOption: null,
            toonOption: null,
            bunkBracketOption: null,
            bunkCoveringOption: null,
            crossArmId: null,
            placeQuantityId: null,
            winchStandOptionId: null,
            axleSuspension: 'Leaf Spring',
        };
        this.partNumber = ['XXX', 'XX', 'XXX', 'X', 'X', 'X'];
        this.generatePartNumber();
        this.matchingParts = [];
    }

    async add() {
        if (this.partsGrid?.instance.getSelectedRowsData().length === 0) return;
        const selectedParts = this.partsGrid.instance.getSelectedRowsData();
        const selectedPart = selectedParts[0];
        let partsSelected = 0;
        console.log(selectedParts);

        const salesAddonIds = (this.partSelection.size?._salesAddons ?? []).map((x) => x.entityId);
        const salesAddonParts = (await this.uow.parts.byIds(salesAddonIds)) as Part[];
        const selectedTire = this.tires.find((x) => x.id === this.partSelection.tireId);
        const spareTirePart = selectedTire?.spareTire;
        const options = [...salesAddonParts];
        if (spareTirePart && !options.some(part => part.id === spareTirePart.id)) {
            options.push(spareTirePart);
        }

        const modalRef = this.modalService.show(PartOptionDialogComponent, {
            class: 'modal-mdw',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                options: options,
            },
        });

        modalRef.content?.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                this.selectedParts.push(selectedPart);
                result.value.forEach((selectedOption: any) => {
                    this.selectedParts.push({
                        id: selectedOption.id,
                        parentId: selectedPart.id,
                        partNumber: selectedOption.partNumber,
                        description: selectedOption.mfgDescription,
                        price: selectedOption.price,
                        isOption: true,
                    });
                });

                if (partsSelected > 0) {
                    this.showSideBar = true;
                    this.appInfo.notifyToast(`${partsSelected} parts have been added`, 'success', 2000);
                }

                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });

        // if (
        //     selectedParts.filter((part) => {
        //         return part.id === this.invalidId;
        //     }).length > 0
        // ) {
        //     alert(`Part cannot be added to itself`, `${this.appInfo.settings.company}`);
        //     return;
        // }

        // if (closeOnSelect) this.closeDialog();
    }

    removeFromSelected(info: any) {
        const index = this.selectedParts.indexOf(info);
        if (index >= 0) this.selectedParts.splice(index, 1);
        if (this.selectedParts.length === 0) {
        }
    }

    toggleUpgrades() {
        this.upgradesEnabled = !this.upgradesEnabled;
        if (!this.upgradesEnabled) {
            this.partSelection.winchStandId = this.partSelection?.size?.standardWinchStandId;
            if (this.partSelection?.size?.standardWinchStandOptionId) {
                this.partSelection.winchStandOptionId = this.partSelection?.size?.standardWinchStandOptionId;
            }
        }
    }

    clearModelType() {
        this.onModelTypeChanged({ value: null });
    }

    async clearSize() {
        this.partSelection.sizeId = null;
        this.partSelection.size = null;
        await this.onSizeChanged({ value: null });
    }

    clearAxle() {
        this.partSelection.axleId = null;
        this.partSelection.brakeCount = null;
        this.partSelection.brakeTypeId = null;
        this.partSelection.brakeType = null;
        this.partSelection.brake1Id = null;
        this.partSelection.brake2Id = null;
        this.partSelection.brake3Id = null;
        this.partNumber[2] = 'XXX';
        this.generatePartNumber();
    }

    clearBrakeCount() {
        this.partSelection.brakeCount = null;
        this.partSelection.brakeTypeId = null;
        this.partSelection.brakeType = null;
        this.partSelection.brake1Id = null;
        this.partSelection.brake2Id = null;
        this.partSelection.brake3Id = null;
        this.partNumber[2] = 'XXX';
        this.generatePartNumber();
    }

    clearBrakeType() {
        this.partSelection.brakeTypeId = null;
        this.partSelection.brakeType = null;
        this.partSelection.brake1Id = null;
        this.partSelection.brake2Id = null;
        this.partSelection.brake3Id = null;
        this.partNumber[2] = 'XXX';
        this.generatePartNumber();
    }

    clearTire() {
        this.partSelection.tireId = null;
        this.partNumber[3] = 'X';
        this.generatePartNumber();
    }

    clearSuspension() {
        this.partSelection.suspensionId = null;
        this.partSelection.suspension = null;
        this.suspensionTires = [];
        this.partSelection.tireId = null;
        this.partNumber[2] = 'XXX';
        this.partNumber[3] = 'X';
        this.generatePartNumber();
    }

    clearTailOption() {
        this.partSelection.tailOption = null;
        this.partNumber[4] = 'X';
        this.generatePartNumber();
    }

    clearBunkBracket() {
        this.partSelection.bunkBracketOption = null;
        this.generatePartNumber();
    }

    clearToonOption() {
        this.partSelection.toonOption = null;
        this.partNumber[4] = 'X';
        this.generatePartNumber();
    }

    clearBunkCovering() {
        this.partSelection.bunkCoveringOption = null;
        this.partNumber[5] = 'X';
        this.generatePartNumber();
    }

    clearWinchStand() {
        this.partSelection.winchStandId = null;
        this.partSelection.winchStand = null;
        this.partSelection.winchStandOptionId = null;
        this.partNumber[4] = 'X';
        this.partNumber[5] = 'X';
        this.generatePartNumber();
    }

    clearWinchStandOption() {
        this.partSelection.winchStandOptionId = null;
        this.partNumber[5] = 'X';
        this.generatePartNumber();
    }

    clearPlaceQty() {
        this.partSelection.placeQuantityId = null;
        this.partNumber[4] = 'X';
        this.generatePartNumber();
    }

    clearCrossArm() {
        this.partSelection.crossArmId = null;
        this.partNumber[5] = 'X';
        this.generatePartNumber();
    }

    clearConfigOptions() {
        this.partSelection.optionsId = null;
        this.partNumber[5] = 'X';
        this.generatePartNumber();
    }

    async save() {
        this.processing = true;
        const selectedPartIds = this.selectedParts.map((x) => {
            return x.id;
        });
        this.result.next({ success: this.selectedParts.length > 0 ? true : false, selectedParts: this.selectedParts.length > 0 ? this.selectedParts : null });
    }

    close() {
        this.uow.rollback();
        this.result.next({ success: false });
    }
}
